/**
 * 登録直後に `/onboarding` へ向かったことの控え。
 * WebView の開き直しなどで遷移が失われてホーム画面（`/discover` または `/recommend`）に着いたとき、一度だけ戻す。
 */

import { getHomePath } from "~/lib/applicationId";

const RECORD_KEY = "pendingOnboarding";

/** 控えの有効時間 */
const WINDOW_MS = 30 * 60 * 1000;

type PendingOnboardingRecord = {
  userId: string;
  markedAt: number;
};

/** 登録直後に `/onboarding` へ向かうときに呼ぶ。 */
export const markPendingOnboarding = (userId: string): void => {
  if (!userId) return;
  try {
    localStorage.setItem(
      RECORD_KEY,
      JSON.stringify({ userId, markedAt: Date.now() }),
    );
  } catch {
    // localStorage 不可: 開き直されたときに戻せないだけなので無視
  }
};

/**
 * 登録直後の遷移先。オンボーディングへ向かうときは控えを残す。
 * 登録画面は履歴に残さない（アプリ内では戻る先が無くなり、戻る操作でオンボーディングを抜けられない）。
 */
export const resolvePostRegisterNavigation = (
  userId: string,
  skipOnboarding: boolean,
) => {
  if (skipOnboarding) return { to: getHomePath(), replace: true } as const;
  markPendingOnboarding(userId);
  return { to: "/onboarding", replace: true } as const;
};

/** 好みを送信したら呼ぶ。 */
export const clearPendingOnboarding = (): void => {
  try {
    localStorage.removeItem(RECORD_KEY);
  } catch {
    // 無視
  }
};

const isValidRecord = (raw: string, userId: string | null): boolean => {
  const parsed = JSON.parse(raw) as Partial<PendingOnboardingRecord>;
  if (!userId || parsed.userId !== userId) return false;
  const { markedAt } = parsed;
  if (typeof markedAt !== "number" || !Number.isFinite(markedAt)) {
    return false;
  }
  const elapsed = Date.now() - markedAt;
  return elapsed >= 0 && elapsed < WINDOW_MS;
};

/** 控えがこのユーザーの有効なものなら true。控えは消さない。 */
export const hasPendingOnboarding = (userId: string | null): boolean => {
  try {
    const raw = localStorage.getItem(RECORD_KEY);
    return raw ? isValidRecord(raw, userId) : false;
  } catch {
    return false;
  }
};

/** 控えがこのユーザーの有効なものなら true。結果に関わらず控えは消す。 */
export const consumePendingOnboarding = (userId: string | null): boolean => {
  try {
    const raw = localStorage.getItem(RECORD_KEY);
    if (!raw) return false;
    localStorage.removeItem(RECORD_KEY);
    return isValidRecord(raw, userId);
  } catch {
    return false;
  }
};
